/**
 * Represents a generic SDK error object.
 */
export interface SdkError {
  code?: string;
  message: string;
  field?: string;
  rules?: Array<{ message: string; format?: string[] }>; // Password complexity rules
  [key: string]: unknown; // Allow other properties with unknown type
}

/**
 * Finds and returns the error message for a specific field from a list of SDK errors.
 * @param fieldName The name of the field to find an error for.
 * @param errors An array of SDK error objects.
 * @returns The error message string if found, otherwise undefined.
 */
export const getFieldError = (
  fieldName: string,
  errors: Array<{ field?: string; message: string }>
): string | undefined => {
  if (!Array.isArray(errors)) {
    return undefined;
  }
  const error = errors.find((err) => err.field === fieldName);
  return error?.message;
};

/**
 * Auth0 server error codes mapped to our i18n keys. Auth0 renders `message` in
 * the transaction locale, which does not follow the in-page language switch,
 * so known codes are always shown with our own translation instead.
 */
export const SDK_ERROR_CODE_KEYS: Readonly<Record<string, string>> = {
  "wrong-credentials": "errors.wrongPassword",
  "wrong-email-credentials": "errors.wrongPassword",
  "wrong-username-credentials": "errors.wrongPassword",
  "invalid-code": "errors.auth0.invalidCode",
  "invalid-expired-code": "errors.auth0.invalidCode",
  "user-blocked": "errors.auth0.userBlocked",
  "too-many-failures": "errors.auth0.tooManyAttempts",
  "too-many-attempts": "errors.auth0.tooManyAttempts",
  "email-in-use": "errors.auth0.emailInUse",
  "user-exists": "errors.auth0.emailInUse",
  "password-breached": "errors.auth0.passwordBreached",
  "password-history-error": "errors.auth0.passwordHistory",
  "password-too-weak": "errors.auth0.passwordTooWeak",
  "password-policy-error": "errors.auth0.passwordTooWeak",
  "password-contains-user-information": "errors.auth0.passwordTooWeak",
  "invalid-captcha": "errors.auth0.invalidCaptcha",
  "invalid-recaptcha": "errors.auth0.invalidCaptcha",
};

/**
 * Returns the i18n key for an SDK error, or undefined if the code is unknown.
 */
export const getSdkErrorKey = (code: string | undefined): string | undefined =>
  code ? SDK_ERROR_CODE_KEYS[code] : undefined;

/**
 * True when Auth0's own `message` is already in the UI language, i.e. the
 * transaction locale (e.g. "es" or "es-ES") matches the active language.
 * When the locale is unknown (local dev mocks), the message is trusted.
 */
export const isAuth0MessageInLanguage = (
  auth0Locale: string | undefined,
  language: string
): boolean => !auth0Locale || auth0Locale.split("-")[0] === language;
